// Session auth for the embedded app.
//
// GHL owns identity — it hands the iframe an AES-encrypted session blob, which
// our server decrypts (/auth/sso) and exchanges for a short signed JWT. The app
// runs in a SINGLE frame (the one GHL loads), so the handshake + refresh happen
// in-process: no nested iframe, no cross-frame token relay.
//
// The token is held in memory + sessionStorage so it survives a reload within
// the tab, and is cleared when the tab closes. Swap `store` to localStorage if
// you want it to persist across tab closes too.

import axios from "axios";
import env from "../config";

const STORAGE_KEY = "sl_app_token";
const REFRESH_MS = 6 * 60 * 60 * 1000; // refresh well within the 12h token TTL
const store: Storage | null =
  typeof window !== "undefined" ? window.sessionStorage : null;

// Read a JWT's `exp` without verifying the signature — only to avoid handing
// back an obviously-expired token (the server stays the real authority).
const isExpired = (token: string): boolean => {
  try {
    const part = token.split(".")[1];
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
    const { exp } = JSON.parse(json);
    return typeof exp === "number" && exp * 1000 <= Date.now();
  } catch {
    return false; // can't read it → let the server decide
  }
};

const readStored = (): string | null => {
  try {
    const t = store?.getItem(STORAGE_KEY) || null;
    if (t && isExpired(t)) {
      store?.removeItem(STORAGE_KEY);
      return null;
    }
    return t;
  } catch {
    return null;
  }
};

let appToken: string | null = readStored();

export const getAppToken = (): string | null => {
  if (appToken && isExpired(appToken)) setAppToken(null);
  return appToken;
};

export const setAppToken = (token: string | null) => {
  appToken = token;
  try {
    if (token) store?.setItem(STORAGE_KEY, token);
    else store?.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable (private mode / partitioned) — keep in memory */
  }
};

// Ask the GHL parent for the encrypted session blob (one-shot, with timeout).
// Resolves null immediately when we're not embedded (no GHL to answer).
const requestGhlBlob = (): Promise<string | null> =>
  new Promise((resolve) => {
    if (typeof window === "undefined" || window.parent === window) {
      return resolve(null);
    }
    let done = false;
    const onMessage = ({ data }: MessageEvent) => {
      if (done || data?.message !== "REQUEST_USER_DATA_RESPONSE") return;
      done = true;
      window.removeEventListener("message", onMessage);
      resolve(data.payload || null);
    };
    window.addEventListener("message", onMessage);
    window.parent.postMessage({ message: "REQUEST_USER_DATA" }, "*");
    setTimeout(() => {
      if (!done) {
        window.removeEventListener("message", onMessage);
        resolve(null);
      }
    }, 6000);
  });

// Full handshake: get the blob → exchange it server-side for a signed token +
// the verified account. Stores the token and returns { token, accountId }, or
// null if we're not embedded / GHL denies / the app isn't installed.
export const authenticateWithGhl = async (): Promise<{
  token: string;
  accountId: string;
} | null> => {
  const blob = await requestGhlBlob();
  if (!blob) return null;
  try {
    const { data } = await axios.post(`${env.API_URL}/auth/sso`, {
      ssoToken: blob,
    });
    const token = data?.data?.token;
    const accountId = data?.data?.account?.accountId;
    if (token && accountId) {
      setAppToken(token);
      return { token, accountId };
    }
  } catch {
    /* fall through */
  }
  return null;
};

// Keep the session fresh in the background — same frame, so a plain timer
// re-handshakes GHL directly (no cross-frame relay). Idempotent.
let refreshTimer: ReturnType<typeof setInterval> | null = null;
export const startAutoRefresh = () => {
  if (typeof window === "undefined" || refreshTimer) return;
  refreshTimer = setInterval(() => {
    authenticateWithGhl();
  }, REFRESH_MS);
};
